<template>
  <v-container v-if="$can('browse-sales', 'YachtModel')">
    <v-row
      align="center"
      no-gutters
      class="mb-4"
    >
      <v-col>
        <div class="headline">
          Yacht Models / Pricelist
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <v-row
            dense
          >
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.id"
                solo
                label="Yacht Model ID"
                hide-details
                clearable
                dense
                type="number"
                autocomplete="no"
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.yacht_manufacturer_id"
                solo
                label="Yacht Manufacturer"
                :items="yachtManufacturers.data"
                :loading="yachtManufacturers.loading"
                item-text="name"
                item-value="id"
                dense
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.label"
                solo
                label="Model Name"
                hide-details
                clearable
                dense
                autocomplete="no"
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.country_id"
                solo
                label="Charter Country"
                :items="countries.data"
                :loading="countries.loading"
                item-text="name"
                item-value="id"
                dense
                autocomplete="disable"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.yacht_type_id"
                solo
                label="Yacht Type"
                :items="yachtTypes.data"
                :loading="yachtTypes.loading"
                item-text="label"
                item-value="id"
                dense
                hide-details
                clearable
              />
            </v-col>
          </v-row>
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Price
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              <v-btn-toggle
                v-model="filtersDirty.currency"
                color="primary"
              >
                <v-btn
                  small
                  value="EUR"
                >
                  EUR
                </v-btn>
                <v-btn
                  small
                  value="USD"
                >
                  USD
                </v-btn>
                <v-btn
                  small
                  value="SEK"
                >
                  SEK
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="pr-1">
              <currency-input
                v-model="filtersDirty.sale_price_from"
                solo
                label="From"
                hide-details
                dense
                autocomplete="no"
              />
            </v-col>
            <v-col class="pl-1">
              <currency-input
                v-model="filtersDirty.sale_price_to"
                solo
                label="To"
                hide-details
                dense
                clearable
                autocomplete="no"
              />
            </v-col>
          </v-row>
          <v-subheader>
            Cabins
          </v-subheader>
          <v-row no-gutters>
            <v-col class="pr-1">
              <v-text-field
                v-model="filtersDirty.cabins_from"
                solo
                label="From"
                hide-details
                dense
                clearable
                autocomplete="no"
              />
            </v-col>
            <v-col class="pl-1">
              <currency-input
                v-model="filtersDirty.cabins_to"
                solo
                label="To"
                hide-details
                dense
                clearable
                autocomplete="no"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="13"
      >
        <div>
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
            class="mb-1 align-center"
          />
          <!--</editor-fold>-->
          <!--<editor-fold desc="Data">-->
          <v-data-table
            :items="yachtModels.data"
            :page.sync="pagination.page"
            :sort-by.sync="sorting.sortBy"
            :sort-desc.sync="sorting.desc"
            :headers="tableHeaders"
            :loading="yachtModels.loading"
            :server-items-length="yachtModels.meta.total"
            item-key="id"
            must-sort
            hide-default-footer
            class="elevation-5 v-table--fixed-header"
          >
            <template v-slot:item="{ item: yachtModel }">
              <yacht-model-pricelist-row
                :yacht-model="yachtModel"
                @view="openView"
                @generate-offer="openView"
                @start-sales="openNewYacht"
              />
            </template>
            <template v-slot:no-data="{}">
              <!--<editor-fold desc="Filtered">-->
              <div
                v-if="isFiltered"
                class="text-center py-4"
              >
                <div>
                  There are no results for your search.
                </div>
                <div
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    @click="clearFilters"
                  >
                    <v-icon left>
                      clear
                    </v-icon>
                    Clear Filters
                  </v-btn>
                </div>
              </div>
              <!--</editor-fold>-->
              <!--<editor-fold desc="NoData">-->
              <div
                v-else-if="!yachtModels.loading"
                class="text-center py-4"
              >
                <v-col cols="16">
                  There are no models at the moment.
                </v-col>
              </div>
              <!--</editor-fold>-->
              <div
                v-else
                class="text-center py-4"
              >
                Loading...
              </div>
            </template>
          </v-data-table>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
            class="align-center"
          />
          <!--</editor-fold>-->
        </div>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-model-view-dialog
      v-if="viewYachtModelDialog.id"
      :yacht-model-id="viewYachtModelDialog.id"
      :country-id="viewYachtModelDialog.countryId"
      :visible.sync="viewYachtModelDialog.visible"
    />

    <yacht-edit-dialog
      :visible.sync="yachtEdit.visible"
      :yacht-model-id="yachtEdit.modelId"
      :country-id="yachtEdit.countryId"
      @update:yacht="yachtCreated"
    />
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import filterableMixin from '@/mixins/filterableMixin'
import paginationMixin from '@/mixins/paginationMixin'
import YachtModel from '@/models/YachtModel'
import YachtModelPricelistRow from '@/components/business/yachtModels/YachtModelPricelistRow'
import YachtModelViewDialog from '@/components/business/yachtModels/YachtModelViewDialog'
import YachtEditDialog from '@/components/business/yachts/YachtEditDialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'

export default {
  name: 'YachtModelsIndex',
  components: {
    CurrencyInput,
    YachtEditDialog,
    YachtModelViewDialog,
    YachtModelPricelistRow
  },
  mixins: [paginationMixin, filterableMixin],
  data() {
    return {
      yachtModels: {
        data: [],
        loading: false,
        meta: {}
      },
      pagination: {
        perPage: 50
      },
      filtersClean: cloneDeep(YachtModel.emptyFilters),
      filtersDirty: cloneDeep(YachtModel.emptyFilters),
      viewYachtModelDialog: {
        visible: false,
        id: undefined,
        countryId: undefined
      },
      yachtEdit: {
        visible: false,
        modelId: undefined,
        countryId: undefined
      }
    }
  },
  computed: {
    tableHeaders() {
      return [
        { sortable: false, class: 'pr-0', width: '80px' },
        { text: 'Yacht Model', value: 'full_label', class: 'pl-5 pr-0' },
        { text: 'Model Year', value: 'model_year' },
        { text: 'For Country', value: 'country_id' },
        { text: 'Price', value: 'sale_price' },
        { sortable: false }
      ]
    },
    countries() {
      return this.$store.getters['settings/charteringCountries']
    },
    ...mapGetters('settings', ['yachtTypes', 'yachtManufacturers'])
  },
  watch: {
    includeDraftsFilter: function (value) {
      if (!value) {
        this.filtersDirty.status = YachtModel.STATUS_ACTIVE
        return
      }

      this.filtersDirty.status = null
    }
  },
  created() {
    this.yachtModelStatuses = YachtModel

    this.filtersDirty.status = YachtModel.STATUS_ACTIVE
    this.filtersDirty.is_sales_active = true

    this.sorting.sortBy = 'full_label'
    this.sorting.desc = false

    this.filtersDirty.model_year_from = null

    this.fetchYachtModels()
  },
  methods: {
    onPaginationUpdate() {
      this.fetchYachtModels()
    },
    onFiltersUpdate() {
      this.resetPagination()
    },
    async fetchYachtModels() {
      this.yachtModels.loading = true

      let queryParams = {
        ...this.paginationQueryParams,
        ...this.filterQueryParams
      }

      try {
        let result = await YachtModel.api.fetchPerCountry(queryParams)

        this.yachtModels.data = result.data
        this.yachtModels.meta = result.meta
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.yachtModels.loading = false
      }
    },
    openView(yachtModel) {
      this.viewYachtModelDialog.id = yachtModel.id
      this.viewYachtModelDialog.countryId = yachtModel.country.id
      this.viewYachtModelDialog.visible = true
    },
    clearFilters() {
      this.filters = cloneDeep(YachtModel.emptyFilters)
    },
    openNewYacht(yachtModel) {
      this.yachtEdit.modelId = yachtModel.id
      this.yachtEdit.countryId = yachtModel.country.id
      this.yachtEdit.visible = true
    },
    yachtCreated(yacht) {
      this.$router.push({ name: 'yachtDashboard', params: { id: yacht.id } })
    }
  }
}
</script>
